import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Tooltip } from 'reactstrap';
import './CustomTabs.css';

interface TabItem {
  id: string;
  label: string | React.ReactNode;
  tooltip?: string;
}

interface CustomTabsProps {
  tabs: TabItem[];
  activeTab: string;
  onTabChange: (tabId: string) => void;
}

interface UnderlineStyle {
  left: number;
  width: number;
}

const CustomTabs: React.FC<CustomTabsProps> = ({ tabs, activeTab, onTabChange }) => {
  const [underlineStyle, setUnderlineStyle] = useState<UnderlineStyle>({ left: 0, width: 0 });
  const [tooltipOpen, setTooltipOpen] = useState<{ [key: string]: boolean }>({});
  const tabRefs = useRef<{ [key: string]: React.RefObject<HTMLDivElement> }>({});

  tabs.forEach((tab) => {
    if (!tabRefs.current[tab.id]) {
      tabRefs.current[tab.id] = React.createRef<HTMLDivElement>();
    }
  });

  const updateUnderlinePosition = useCallback(() => {
    const activeTabRef = tabRefs.current[activeTab];

    if (activeTabRef?.current) {
      const { offsetLeft, offsetWidth } = activeTabRef.current;
      setUnderlineStyle({
        left: offsetLeft,
        width: offsetWidth,
      });
    }
  }, [activeTab]);

  useEffect(() => {
    updateUnderlinePosition();
  }, [updateUnderlinePosition]);

  const toggleTooltip = (tooltipId: string) => {
    setTooltipOpen(prev => ({ ...prev, [tooltipId]: !prev[tooltipId] }));
  };

  return (
    <div className="custom-tabs">
      <div className="custom-tabs-nav">
        {tabs.map((tab) => {
          const tooltipId = `tab-tooltip-${tab.id}`;
          return (
            <div
              key={tab.id}
              ref={tabRefs.current[tab.id]}
              className="custom-tab-item"
            >
              <button
                className={`custom-tab-button ${activeTab === tab.id ? 'active' : ''}`}
                onClick={() => onTabChange(tab.id)}
              >
                {tab.label}
              </button>
              {tab.tooltip && (
                <>
                  <button
                    type="button"
                    id={tooltipId}
                    className="custom-tab-tooltip-icon"
                    onMouseEnter={() => toggleTooltip(tooltipId)}
                    onMouseLeave={() => toggleTooltip(tooltipId)}
                    onFocus={() => setTooltipOpen(prev => ({ ...prev, [tooltipId]: true }))}
                    onBlur={() => setTooltipOpen(prev => ({ ...prev, [tooltipId]: false }))}
                    onClick={(e) => e.stopPropagation()}
                    aria-label={`Information about ${typeof tab.label === 'string' ? tab.label : tab.id}`}
                  >
                    <i className="fas fa-info-circle" />
                  </button>
                  <Tooltip
                    target={tooltipId}
                    isOpen={tooltipOpen[tooltipId] || false}
                    placement="bottom"
                    autohide={false}
                  >
                    {tab.tooltip}
                  </Tooltip>
                </>
              )}
            </div>
          );
        })}
        <div
          className="custom-tabs-underline"
          style={{
            left: `${underlineStyle.left}px`,
            width: `${underlineStyle.width}px`,
          }}
        />
      </div>
    </div>
  );
};

export default CustomTabs;
